<script setup lang="ts">
import { CurrencyDisplay, formatting, Models, useLocalization, useOrderViewContext } from '@hantera/portal-app';
import { DecimalInput, TextInput, useClose } from '@hantera/design-system';
import { ref, watchEffect } from "vue";
import Decimal from "decimal.js";

const props = defineProps<{
  discount: Models.Graph.Discount
}>();

const t = useLocalization('components.order.discounts');
const tCommon = useLocalization('common');
const orderViewContext = useOrderViewContext();
const close = useClose();

const description = ref(props.discount.description);
const isDirty = ref(false);

const value = ref({
  value: new Decimal(0),
  isPercentage: false
});

watchEffect(() => {
  resetValues();
});

function resetValues() {
  description.value = props.discount.description;
  value.value = {
    value: props.discount.discountType === 'percentage' ? props.discount.value.mul(100) : props.discount.value,
    isPercentage: props.discount.discountType === 'percentage',
  };
}

async function update(closeAfter?: boolean) {
  if (!isDirty.value) {
    if (closeAfter === true) {
      close();
    }
    return;
  }

  if (await orderViewContext.pushPendingChange(
    `staticDiscount_${props.discount.discountId}`,
    [{
      type: 'setDiscountDescription',
      discountId: props.discount.discountId,
      description: description.value ?? '',
    }, {
      type: 'setStaticDiscountValue',
      discountId: props.discount.discountId,
      value: value.value.isPercentage ? value.value.value.div(100) : value.value.value,
      isPercentage: value.value.isPercentage
    }],
    'Updated discount {description} to value {textValue}',
    {
      discountId: props.discount.discountId,
      description: description.value,
      textValue: value.value.isPercentage ?
        formatting.percent(value.value.value) :
        formatting.currency(value.value.value, orderViewContext.state.currencyCode, CurrencyDisplay.none),
      value: value.value.value,
      isPercentage: value.value.isPercentage
    }
  )) {
    isDirty.value = false;

    if (closeAfter === true) {
      close();
    }
  }
}

async function remove() {

  if (await orderViewContext.pushPendingChange(
    `staticDiscount_${props.discount.discountId}`,
    [{
      type: 'deleteDiscount',
      discountId: props.discount.discountId,
    }],
    'Removed discount {description}', {
    description: description.value,
  }
  )) {
    isDirty.value = false;
  }
}

</script>

<template>
  <div class="static-discount">
    <div>
      <label for="discountDescription">{{ t('description') }}</label>
      <TextInput
        v-model="description"
        name="discountDescription"
        @change="isDirty = true"
        @blur="_ => update()"
        @keyenter="_ => update()"
      />
    </div>
    <div>
      <label for="discountValue">{{ t('value') }}</label>
      <DecimalInput
        v-model="value"
        ref="valueElem"
        :select-on-focus="true"
        :min-value="0"
        :allow-percentage="true"
        name="discountValue"
        @change="isDirty = true"
        @blur="_ => update()"
        @keyenter="_ => update()"
      />
    </div>
    <div class="static-discount__actions">
      <button class="btn-link" @click="remove">{{ tCommon('remove') }}</button>
      <button @click="() => update(true)">{{ tCommon('close') }}</button>
    </div>
  </div>
</template>

<style scoped>
.static-discount {
  margin-bottom: 5px;
  display: grid;
  grid-template-columns: auto 100px;
  gap: 5px;
}

.static-discount__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  grid-column: 1 / 3;
}
</style>